import {
  ChangeEvent,
  ClipboardEvent,
  DragEvent,
  useContext,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

import {
  GetMyFeedbackScreenshotUploadUrlsDocument,
  GetMyFeedbackScreenshotUploadUrlsQuery,
  GetMyFeedbackScreenshotUploadUrlsQueryVariables,
  useSubmitFeedbackMutation,
} from "../../services/api/__generated__/backend_gateway-types";
import { FeedbackTicketsView } from "./tickets_view";
import { useApi } from "../../contexts/app/provider";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../contexts/sys_messages/type";
import { VersionedDataContext } from "../../contexts/versioned_data/context";

// Client-side guard rails. Screenshots upload directly to S3 (presigned PUT,
// bypassing API Gateway / WAF), so the GraphQL mutation body no longer carries
// the bytes — these caps are about sane UX, not request-size limits.
const MAX_IMAGES = 4;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB per image

// Optional customer-set urgency. Anchored on IMPACT (what's happening to them),
// not abstract priority labels, to keep the signal honest. Values are Linear
// priorities — 1 Urgent / 3 Medium / 4 Low. High (2) is intentionally NOT
// offered: it's reserved for Atomic's triage. Unset → No priority.
const IMPACT_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Blocking — I can't get my work done" },
  { value: 3, label: "Disrupting — something's wrong, but I have a workaround" },
  { value: 4, label: "Minor — a suggestion or no rush" },
];

type FeedbackImg = {
  id: string;
  file: File; // raw bytes, PUT straight to S3
  filename: string;
  previewUrl: string; // object URL for the thumbnail; revoked on remove/reset
};

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Site-wide "Report a problem" modal, opened from the left-rail icon. Submits a
 * free-text description to the `submitFeedback` mutation, which files a Linear
 * issue (with the submitter's email / customer / page URL auto-attached).
 *
 * Screenshots are uploaded directly to S3 via presigned PUT URLs
 * (getMyFeedbackScreenshotUploadUrls), then referenced by their S3 keys in the
 * mutation. This keeps the mutation body tiny so large images never trip the
 * WAF body-size limit, and the backend relays the images to Linear.
 *
 * Graceful degradation: if feedback delivery is unavailable (Linear creds not
 * yet seeded, API unreachable), the mutation returns success:false and we show
 * an inline "reach out to Atomic directly" fallback WITHOUT clearing the user's
 * text, so nothing they typed is lost.
 */
export const FeedbackModal = ({
  open,
  onClose,
}: FeedbackModalProps): JSX.Element => {
  const theme = useTheme();
  const { apiService } = useApi();
  const { addSystemMessage } = useContext(SysMessagesContext);
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const activeRun = mainRun || lastSuccessfulRun;

  // "form" = report a problem; "tickets" = read-only "See tickets" list.
  const [view, setView] = useState<"form" | "tickets">("form");
  // Tickets view only: maximize grows the dialog for comfortable reading.
  const [dialogExpanded, setDialogExpanded] = useState(false);
  const [title, setTitle] = useState("");
  // "" = not specified (optional). Otherwise a Linear priority from IMPACT_OPTIONS.
  const [impact, setImpact] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<FeedbackImg[]>([]);
  const [imageNote, setImageNote] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // atomic-webapp doesn't wrap the tree in <ApolloProvider> — every Apollo
  // hook callsite must pass the client explicitly (see e.g. my_team/container.tsx).
  // Without this, useSubmitFeedbackMutation throws Apollo invariant #58
  // ("Could not find 'client' in the context") and blanks the page.
  const [submitFeedback] = useSubmitFeedbackMutation({
    client: apiService.getClient(),
  });

  const reset = () => {
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    setTitle("");
    setImpact("");
    setDescription("");
    setImageNote(null);
    setDragActive(false);
    setView("form");
    setDialogExpanded(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  // Wrap dropped/picked/pasted files as previews, enforcing type / size / count
  // limits. Shared by the file picker, clipboard paste, and drag-drop.
  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    const notes: string[] = [];
    const accepted: FeedbackImg[] = [];
    for (const file of files) {
      if (!file.type.startsWith("image/") || file.size === 0) continue;
      if (file.size > MAX_IMAGE_BYTES) {
        notes.push(`${file.name || "An image"} is larger than 5 MB and was skipped.`);
        continue;
      }
      accepted.push({
        id: crypto.randomUUID(),
        file,
        filename: file.name || `screenshot-${Date.now()}.png`,
        previewUrl: URL.createObjectURL(file),
      });
    }
    // Read count from current state *before* the update — pushing it into the
    // setImages updater would run lazily (and twice under StrictMode) so the
    // setImageNote below would miss it. These interactions are sequential, so
    // closing over `images` is safe.
    const room = MAX_IMAGES - images.length;
    if (accepted.length > room) {
      notes.push(`Only ${MAX_IMAGES} screenshots can be attached.`);
      // Revoke the previews we're about to drop so they don't leak.
      accepted.slice(Math.max(0, room)).forEach((img) =>
        URL.revokeObjectURL(img.previewUrl)
      );
    }
    setImages((prev) => [...prev, ...accepted.slice(0, Math.max(0, room))]);
    setImageNote(notes.length > 0 ? notes.join(" ") : null);
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const match = prev.find((img) => img.id === id);
      if (match) URL.revokeObjectURL(match.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
    setImageNote(null);
  };

  // Presign + PUT each screenshot directly to S3, returning the S3 keys of the
  // ones that uploaded successfully. A failed upload is non-fatal — the text
  // feedback still files with whatever images made it.
  const uploadScreenshots = async (toUpload: FeedbackImg[]): Promise<string[]> => {
    if (toUpload.length === 0) return [];
    const client = apiService.getClient();
    const { data } = await client.query<
      GetMyFeedbackScreenshotUploadUrlsQuery,
      GetMyFeedbackScreenshotUploadUrlsQueryVariables
    >({
      query: GetMyFeedbackScreenshotUploadUrlsDocument,
      variables: { filenames: toUpload.map((img) => img.filename) },
      fetchPolicy: "no-cache",
    });
    const credentials = data?.getMyFeedbackScreenshotUploadUrls ?? [];

    const results = await Promise.allSettled(
      toUpload.map(async (img, i) => {
        const cred = credentials[i];
        if (!cred) throw new Error("no presigned URL");
        const resp = await fetch(cred.uploadUrl, {
          method: "PUT",
          body: img.file,
          headers: { "Content-Type": img.file.type },
        });
        if (!resp.ok) {
          throw new Error(`S3 PUT failed: ${resp.status}`);
        }
        return cred.s3Key;
      })
    );

    const keys: string[] = [];
    let failures = 0;
    for (const r of results) {
      if (r.status === "fulfilled") keys.push(r.value);
      else failures += 1;
    }
    if (failures > 0) {
      setImageNote(
        `${failures} screenshot(s) couldn't be uploaded and were left out.`
      );
    }
    return keys;
  };

  // Runs after the modal has already closed: upload screenshots to S3, file the
  // feedback, and report the outcome via a toast. Detached from the modal so the
  // user is never blocked waiting on the S3 + Linear round-trips.
  const sendInBackground = async (
    text: string,
    issueTitle: string,
    issuePriority: number | "",
    imgs: FeedbackImg[]
  ) => {
    const dismissProgress = addSystemMessage({
      level: SysMessageLevel.EMPHASIZED,
      message: "Sending your feedback…",
      keepOpen: true,
    });
    try {
      const screenshotKeys =
        imgs.length > 0 ? await uploadScreenshots(imgs) : [];
      const result = await submitFeedback({
        variables: {
          description: text,
          // Empty → backend derives the title from the first line of the body.
          title: issueTitle || null,
          // "" → no urgency selected; backend leaves it at No priority.
          priority: typeof issuePriority === "number" ? issuePriority : null,
          pageUrl: window.location.href,
          runId: activeRun?.id ?? null,
          runDisplayId: activeRun?.displayId ?? null,
          screenshotKeys,
        },
      });
      dismissProgress();
      if (result.data?.submitFeedback?.success) {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Thanks — your feedback was sent.",
        });
      } else {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message:
            "Feedback couldn't be sent. Please try again, or reach out to the Atomic team directly.",
        });
      }
    } catch {
      dismissProgress();
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message:
          "Feedback couldn't be sent. Please try again, or reach out to the Atomic team directly.",
      });
    }
  };

  // Optimistic submit: capture the input, close the modal immediately so the
  // user isn't blocked, then do the upload + filing in the background. The
  // captured `imgs` hold the File objects, so close()'s revoke of the thumbnail
  // object URLs doesn't affect the upload.
  const handleSend = () => {
    const text = description.trim();
    const issueTitle = title.trim();
    if (!text || !issueTitle) return;
    const imgs = images;
    const issuePriority = impact;
    close();
    void sendInBackground(text, issueTitle, issuePriority, imgs);
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(e.clipboardData.files);
    if (files.some((f) => f.type.startsWith("image/"))) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const onFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = ""; // allow re-picking the same file
  };

  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragActive(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    // Ignore leave events fired when moving over child nodes.
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragActive(false);
    }
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const tickets = view === "tickets";

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={tickets && dialogExpanded ? false : "sm"}
      fullWidth
      PaperProps={{
        onPaste,
        sx: tickets && dialogExpanded ? { width: "92vw", maxWidth: "92vw" } : undefined,
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1,
        }}
      >
        {tickets ? "Submitted tickets" : "Feedback"}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {tickets && (
            <IconButton
              onClick={() => setDialogExpanded((v) => !v)}
              size="small"
              aria-label={dialogExpanded ? "Minimize" : "Maximize"}
              sx={{ color: theme.palette.grey[500] }}
            >
              {dialogExpanded ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </IconButton>
          )}
          <IconButton
            onClick={close}
            size="small"
            sx={{ color: theme.palette.grey[500] }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {tickets ? (
          <FeedbackTicketsView expanded={dialogExpanded} />
        ) : (
          <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tell us what's working, what's not, or what you'd like to see — we'll
          get it to the Atomic team. Your email and the current page are
          attached automatically.
        </Typography>

        <TextField
          label="Issue title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          autoFocus
          inputProps={{ maxLength: 80 }}
          placeholder="e.g. Shipments chart empty after date-range change"
          sx={{ mb: 2 }}
        />

        <TextField
          label="Your feedback"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={5}
          fullWidth
          placeholder="e.g. The shipments chart shows no data after I change the date range."
          sx={{ mb: 2 }}
        />

        <TextField
          select
          label="How is this affecting you? (optional)"
          value={impact}
          onChange={(e) =>
            setImpact(e.target.value === "" ? "" : Number(e.target.value))
          }
          fullWidth
          sx={{ mb: 2 }}
        >
          <MenuItem value="">Not specified</MenuItem>
          {IMPACT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <Box
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.5,
            py: 1.5,
            minHeight: "3.25rem",
            border: "1px dashed",
            borderColor: dragActive
              ? theme.palette.primary.main
              : theme.palette.divider,
            backgroundColor: dragActive
              ? "rgba(37, 99, 235, 0.06)"
              : theme.palette.grey[50],
            borderRadius: 1,
            transition: "border-color 0.12s ease, background 0.12s ease",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={onFileInputChange}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={images.length >= MAX_IMAGES}
          >
            Attach screenshot
          </Button>
          <Typography variant="caption" color="text.secondary">
            or paste / drag an image here (up to {MAX_IMAGES})
          </Typography>
        </Box>

        {images.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
            {images.map((img) => (
              <Box
                key={img.id}
                sx={{
                  position: "relative",
                  width: "4rem",
                  height: "4rem",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <img
                  src={img.previewUrl}
                  alt={img.filename}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <IconButton
                  size="small"
                  aria-label={`Remove ${img.filename}`}
                  onClick={() => removeImage(img.id)}
                  sx={{
                    position: "absolute",
                    top: 1,
                    right: 1,
                    width: 18,
                    height: 18,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {imageNote && (
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: theme.palette.warning.dark }}>
            {imageNote}
          </Typography>
        )}
          </>
        )}
      </DialogContent>

      {tickets ? (
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setView("form");
              setDialogExpanded(false);
            }}
          >
            Back
          </Button>
        </DialogActions>
      ) : (
        <DialogActions sx={{ p: 2, gap: 1, justifyContent: "space-between" }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setView("tickets")}
          >
            See tickets
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSend}
              disabled={
                title.trim().length === 0 || description.trim().length === 0
              }
            >
              Send feedback
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};
